import clsx from "clsx";
import type { FunctionComponent, HTMLAttributes, ReactNode } from "react";
import Loader from "../loader/loader";

export type WeightIconType =
  | "thin"
  | "light"
  | "regular"
  | "bold"
  | "fill"
  | "duotone";

const colorClasses = {
  lime: {
    primary:
      "bg-lime-100 hover:bg-lime-80 text-black focus-visible:outline-lime-100",
    secondary:
      "border-lime-60 border-2 text-lime-100 hover:border-lime-40 hover:text-lime-80 focus-visible:outline-lime-60",
    tertiary:
      "bg-lime-10 hover:bg-lime-20 text-lime-100 focus-visible:outline-lime-10",
    quaternary: "bg-transparent text-lime-100 hover:text-lime-70",
  },
  indigo: {
    primary:
      "bg-indigo-100 hover:bg-indigo-80 text-white focus-visible:outline-indigo-100",
    secondary:
      "border-indigo-60 border-2 hover:border-indigo-40 text-indigo-100 hover:text-indigo-80 focus-visible:outline-indigo-60",
    tertiary:
      "bg-indigo-10 hover:bg-indigo-20 text-indigo-100 focus-visible:outline-indigo-10",
    quaternary: "bg-transparent text-indigo-100 hover:text-indigo-70",
  },
  white: {
    primary:
      "bg-white hover:bg-white-80 text-black focus-visible:outline-white",
    secondary:
      "border-white-60 border-2 hover:border-white-40 text-white hover:text-white-80 focus-visible:outline-white-60",
    tertiary:
      "bg-white-10 hover:bg-white-20 text-white focus-visible:outline-white-10",
    quaternary: "bg-transparent text-white hover:text-white-70",
  },
};

const common = (disabled: boolean, buttonType: string) =>
  clsx(
    `inline-flex text-nowrap justify-center items-center rounded-lg focus-visible:outline-1 focus:outline-none  ${
      disabled
        ? buttonType === "quaternary"
          ? "text-white-40"
          : "bg-white-10 text-white-40 cursor-not-allowed"
        : "cursor-pointer"
    }`,
  );

const sizeType = {
  sm: "text-textSm h-8 px-3 py-2 gap-x-2 font-matterMedium",
  base: "text-textBase h-10 px-4 py-2 gap-x-2 font-matterMedium",
  lg: "text-textBase h-11 px-[18px] py-3 gap-x-2 font-matterMedium",
};

const sizeIconType = {
  sm: 12,
  base: 16,
  lg: 20,
};

interface ButtonProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  label?: string;
  buttonType: "primary" | "secondary" | "tertiary" | "quaternary";
  size?: keyof typeof sizeType;
  type?: "submit" | "button";
  color: keyof typeof colorClasses;
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: FunctionComponent<{
    size?: number | string;
    mirrored: boolean;
    weight: WeightIconType;
    className?: string;
  }>;
  rightIcon?: FunctionComponent<{
    size?: number | string;
    mirrored: boolean;
    weight: WeightIconType;
    className?: string;
  }>;
  onClick?: () => void;
  mirrored?: boolean;
  weightIcon?: WeightIconType;
}

export default function Button({
  children,
  label,
  className,
  buttonType,
  size = "base",
  type = "button",
  color = "lime",
  loading = false,
  disabled = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onClick,
  mirrored = false,
  weightIcon = "regular",
  ...props
}: ButtonProps) {
  const disabledButton = loading || disabled;

  const buttonClasses = !disabledButton ? colorClasses[color][buttonType] : "";

  const classNames = clsx(
    common(disabledButton, buttonType),
    sizeType[size],
    buttonClasses,
    className,
  );

  const classNamesIcons = clsx("bg-transparent border-none");
  const sizeIcon = clsx(sizeIconType[size]);

  return (
    <button
      className={classNames}
      disabled={disabledButton}
      type={type}
      onClick={onClick}
      {...props}
    >
      {loading && <Loader />}
      {LeftIcon && (
        <LeftIcon
          size={sizeIcon}
          mirrored={mirrored}
          weight={weightIcon}
          className={classNamesIcons}
        />
      )}
      {children}
      {label}
      {RightIcon && (
        <RightIcon
          size={sizeIcon}
          mirrored={mirrored}
          weight={weightIcon}
          className={classNamesIcons}
        />
      )}
    </button>
  );
}
